import { Product } from '@shared/components/ui/Product';
import { MapPin, ShoppingCart } from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { EntryProductInfo } from '../../models/Receive';
import { useProductLocations } from '../../hooks/useProductLocations';

/** Asignación de producto a carrito(s): código de carrito y cantidad recibida en él */
export type ProductCartAssignment = { code: string; qty: number };

interface EntryProductsListProps {
  products: EntryProductInfo[];
  scannedProducts?: Record<number, { scannedQty: number }>; // Opcional, para compatibilidad
  /** Mapa product_id -> carrito(s) donde se asignó el producto (para mostrar "en qué carrito quedó") */
  productCartAssignments?: Record<number, ProductCartAssignment[]>;
}

const buildImageUrl = (image?: string | null): string => {
  if (!image) return '';
  if (image.startsWith('http')) return image;
  return `https://warehouse-images-stg-dev.nyc3.digitaloceanspaces.com/${image}`;
};

/** Ubicaciones del producto: icono + código y stock; se usa en pendientes y completados, inline junto al carrito en completados */
const ProductLocationsRow: React.FC<{ productId: number }> = ({ productId }) => {
  const { t } = useTranslation('merchandiseV3');
  const { locations, isLoading } = useProductLocations(productId);
  if (isLoading || locations.length === 0) return null;
  return (
    <>
      {locations.map((loc) => (
        <span key={loc.id} className="flex items-center gap-1 font-mono">
          <MapPin className="w-3 h-3 text-icons" />
          {loc.code} ({loc.stock} {t('receive.quantity_confirm.units', 'ud.')})
        </span>
      ))}
    </>
  );
};

export const EntryProductsList: React.FC<EntryProductsListProps> = ({
  products,
  scannedProducts = {},
  productCartAssignments = {},
}) => {
  const { t } = useTranslation('merchandiseV3');

  if (!products || products.length === 0) {
    return (
      <div className="bg-background border border-border rounded-lg p-4">
        <div className="text-center py-8">
          <div className="text-muted-foreground mb-4">
            <svg
              className="mx-auto h-12 w-12 text-muted-foreground/50"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
              />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-foreground mb-2">
            {t('receive.products.empty.title', 'No hay productos')}
          </h3>
          <p className="text-sm text-muted-foreground">
            {t('receive.products.empty.description', 'Esta entrada no tiene productos asignados.')}
          </p>
        </div>
      </div>
    );
  }

  // Agrupar productos por product_id
  const productGroups = products.reduce(
    (acc, line) => {
      const productId = line.product_id;

      if (!acc[productId]) {
        acc[productId] = {
          productId,
          name: line.product?.name || `Producto ID: ${productId}`,
          code: line.product?.code || '',
          image: buildImageUrl(line.product?.image_hash || line.product?.img || line.product?.image),
          totalExpectedQuantity: 0,
          totalAcceptedQuantity: 0,
          totalRejectedQuantity: 0,
          // Mantener compatibilidad con scannedProducts si existe
          totalScannedQuantity: scannedProducts[productId]?.scannedQty || 0,
        };
      }

      acc[productId].totalExpectedQuantity += line.expected_qty;
      acc[productId].totalAcceptedQuantity += line.qty_accept || 0;
      acc[productId].totalRejectedQuantity += line.qty_rejects || 0;

      return acc;
    },
    {} as Record<
      number,
      {
        productId: number;
        name: string;
        code: string;
        image: string;
        totalExpectedQuantity: number;
        totalAcceptedQuantity: number;
        totalRejectedQuantity: number;
        totalScannedQuantity: number;
      }
    >,
  );

  const productList = Object.values(productGroups).map((group) => {
    // Usar qty_accept + qty_rejects si están disponibles, sino usar scannedQuantity
    const totalProcessed = group.totalAcceptedQuantity + group.totalRejectedQuantity;
    const processedQuantity = totalProcessed > 0 ? totalProcessed : group.totalScannedQuantity;

    return {
      productId: group.productId,
      name: group.name,
      code: group.code,
      image: group.image,
      expectedQuantity: group.totalExpectedQuantity,
      acceptedQuantity: group.totalAcceptedQuantity,
      rejectedQuantity: group.totalRejectedQuantity,
      scannedQuantity: processedQuantity,
      isCompleted: processedQuantity >= group.totalExpectedQuantity,
    };
  });

  const completedProducts = productList.filter((p) => p.isCompleted);
  const pendingProducts = productList.filter((p) => !p.isCompleted);

  return (
    <div className="space-y-6">
      {/* Productos pendientes */}
      {pendingProducts.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-foreground flex items-center space-x-2">
              <span className="text-orange-600 dark:text-orange-400">
                {t('receive.products.pending.title', 'Por escanear')}
              </span>
              <span className="text-sm text-muted-foreground">({pendingProducts.length})</span>
            </h3>
          </div>

          <div className="space-y-4">
            {pendingProducts.map((product) => {
              const remaining = product.expectedQuantity - product.scannedQuantity;
              const progressPercentage = (product.scannedQuantity / product.expectedQuantity) * 100;

              return (
                <div key={product.productId} className="bg-background border border-border rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <Product
                        id={product.productId}
                        name={product.name}
                        src={product.image}
                        code={product.code}
                        showImageModal={true}
                      />
                    </div>

                    {/* Información de cantidades */}
                    <div className="flex flex-col items-end space-y-1 ml-4">
                      <div className="flex items-center space-x-2">
                        <span className="text-lg font-semibold text-orange-600 dark:text-orange-400">
                          {product.scannedQuantity}
                        </span>
                        <span className="text-muted-foreground">/</span>
                        <span className="text-lg font-semibold text-muted-foreground">{product.expectedQuantity}</span>
                      </div>

                      {/* Desglose de aceptados/rechazados */}
                      {(product.acceptedQuantity > 0 || product.rejectedQuantity > 0) && (
                        <div className="flex items-center space-x-2 text-xs">
                          {product.acceptedQuantity > 0 && (
                            <span className="text-green-600 dark:text-green-400">
                              ✓ {product.acceptedQuantity} {t('receive.products.accepted', 'aceptados')}
                            </span>
                          )}
                          {product.rejectedQuantity > 0 && (
                            <span className="text-red-600 dark:text-red-400">
                              ✗ {product.rejectedQuantity} {t('receive.products.rejected', 'rechazados')}
                            </span>
                          )}
                        </div>
                      )}

                      <div className="text-xs text-muted-foreground">
                        {remaining} {t('receive.products.remaining', 'pendientes')}
                      </div>

                      {/* Barra de progreso */}
                      <div className="w-16 h-2 bg-muted rounded-full overflow-hidden">
                        <div
                          className="h-full bg-orange-500 transition-all duration-300"
                          style={{ width: `${progressPercentage}%` }}
                        />
                      </div>
                    </div>
                  </div>
                  {/* Ubicación(es) del producto (también en pendientes, para guiar al operario) */}
                  <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground mt-2">
                    <ProductLocationsRow productId={product.productId} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Productos completados */}
      {completedProducts.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-foreground flex items-center space-x-2">
              <span className="text-green-600 dark:text-green-400">
                {t('receive.products.completed.title', 'Completados')}
              </span>
              <span className="text-sm text-muted-foreground">({completedProducts.length})</span>
            </h3>
          </div>

          <div className="space-y-4">
            {completedProducts.map((product) => (
              <div
                key={product.productId}
                className="bg-background border border-green-200 dark:border-green-800 rounded-lg p-4"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <Product
                      id={product.productId}
                      name={product.name}
                      src={product.image}
                      code={product.code}
                      showImageModal={true}
                    />
                  </div>

                  {/* Información de cantidades */}
                  <div className="flex flex-col items-end space-y-1 ml-4">
                    <div className="flex items-center space-x-2">
                      <span className="text-lg font-semibold text-green-600 dark:text-green-400">
                        {product.scannedQuantity}
                      </span>
                      <span className="text-muted-foreground">/</span>
                      <span className="text-lg font-semibold text-muted-foreground">{product.expectedQuantity}</span>
                    </div>

                    {/* Desglose de aceptados/rechazados */}
                    {(product.acceptedQuantity > 0 || product.rejectedQuantity > 0) && (
                      <div className="flex items-center space-x-2 text-xs">
                        {product.acceptedQuantity > 0 && (
                          <span className="text-green-600 dark:text-green-400">
                            ✓ {product.acceptedQuantity} {t('receive.products.accepted', 'aceptados')}
                          </span>
                        )}
                        {product.rejectedQuantity > 0 && (
                          <span className="text-red-600 dark:text-red-400">
                            ✗ {product.rejectedQuantity} {t('receive.products.rejected', 'rechazados')}
                          </span>
                        )}
                      </div>
                    )}

                    <div className="text-xs text-green-600 dark:text-green-400 font-medium">
                      {t('receive.products.completed.label', 'Completado')}
                    </div>

                    {/* Barra de progreso */}
                    <div className="w-16 h-2 bg-muted rounded-full overflow-hidden">
                      <div className="h-full bg-green-500 transition-all duration-300" style={{ width: '100%' }} />
                    </div>
                  </div>
                </div>
                {/* Ubicación(es) primero, luego carrito(s) asignado(s) */}
                <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground mt-2">
                  <ProductLocationsRow productId={product.productId} />
                  {productCartAssignments[product.productId]?.map(({ code, qty }) => (
                    <span key={code} className="flex items-center gap-1 font-mono">
                      <ShoppingCart className="w-3 h-3 text-icons" />
                      {code} ({qty} {t('receive.quantity_confirm.units', 'ud.')})
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
